import { TextButton, IconButton } from '@creditornot/cb-button';
import { Minus, Plus, Trashbin } from '@creditornot/cb-icons';
import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import CropIcon from './CropIcon';
import DraggableImage from './DraggableImage';
import { MultiImageValue } from './MultiImageUploadInput';
import { Crop, Cropper } from '../ImageCropper';
import { CropperContainer, ZoomControls } from '../styled';
import { useObjectUrl } from '../utils';

type Props = {
  className?: string;
  image: {
    id: string;
    type: 'new-image';
    file: File;
    crop: Crop;
  };
  interactionsDisabled: boolean;
  onCancelCropping: () => void;
  onCropChange: (crop: Crop, image: MultiImageValue) => void;
  onRemove: (imageId: string) => void;
  onStartCropping: () => void;
};

type State =
  | {
      type: 'initial';
    }
  | {
      type: 'cropping';
      crop: Crop;
    };

const MAX_ZOOM = 2;
const ZOOM_STEP = 0.5;

const CropModeContainer = styled.div`
  margin-block-end: 12px;
`;

const StyledCropper = styled(Cropper)``;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const NewImage = ({
  image,
  interactionsDisabled,
  onCancelCropping,
  onCropChange,
  onRemove,
  onStartCropping,
}: Props) => {
  const cropperRef = useRef<React.ElementRef<typeof Cropper>>(null);
  const [state, setState] = useState<State>({ type: 'initial' });

  const objectUrl = useObjectUrl(image.file);

  if (!objectUrl) {
    return null;
  }

  return state.type === 'initial' ? (
    <DraggableImage
      crop={image.crop}
      draggingDisabled={interactionsDisabled}
      imageId={image.id}
      imageUrl={objectUrl}
      rightContent={
        <Actions>
          <IconButton
            disabled={interactionsDisabled}
            size="tiny"
            style={{ marginInlineEnd: '8px' }}
            variant="grey"
            onClick={() => {
              onStartCropping();
              setState({ type: 'cropping', crop: image.crop });
            }}
          >
            <CropIcon />
          </IconButton>
          <IconButton disabled={interactionsDisabled} size="tiny" onClick={() => onRemove(image.id)} variant="red">
            <Trashbin />
          </IconButton>
        </Actions>
      }
    />
  ) : (
    <CropModeContainer>
      <CropperContainer>
        <StyledCropper
          crop={state.crop}
          imageUrl={objectUrl}
          maxZoom={MAX_ZOOM}
          onCropChange={newCrop => {
            setState({ type: 'cropping', crop: newCrop });
          }}
          ref={cropperRef}
        />
        <ZoomControls>
          <IconButton
            background="staticWhite"
            disabled={state.crop.zoom >= MAX_ZOOM}
            onClick={() => {
              if (!cropperRef.current) {
                return;
              }
              cropperRef.current.zoom(state.crop.zoom + ZOOM_STEP);
            }}
            size="tiny"
            variant="lightGrey"
          >
            <Plus />
          </IconButton>
          <IconButton
            background="staticWhite"
            disabled={state.crop.zoom <= 1}
            onClick={() => {
              if (!cropperRef.current) {
                return;
              }
              cropperRef.current.zoom(state.crop.zoom - ZOOM_STEP);
            }}
            size="tiny"
            variant="lightGrey"
          >
            <Minus />
          </IconButton>
        </ZoomControls>
      </CropperContainer>
      <Actions style={{ marginBlockStart: '8px' }}>
        <TextButton
          variant="grey"
          style={{ marginInlineEnd: '8px' }}
          onClick={() => {
            setState({ type: 'initial' });
            onCancelCropping();
          }}
        >
          <FormattedMessage id="image-upload.cancel" />
        </TextButton>
        <TextButton
          onClick={() => {
            onCropChange(state.crop, image);
            setState({ type: 'initial' });
          }}
        >
          <FormattedMessage id="image-upload.applyCrop" />
        </TextButton>
      </Actions>
    </CropModeContainer>
  );
};

export default React.memo(NewImage);
